// MACHINERY V2 (Stroje) — ReadinessPopupV2 sections (M2.9).
//
// Presentational parts of the readiness popup, split out for the same reason
// the shipped v1 feature split `readinessPopupParts.tsx` off
// `MidasReadinessPopup.tsx`: the popup is the biggest surface in the kit and
// both halves must stay under the 300-line rule.
//
// >>> POPUP-ONLY. DO NOT IMPORT ANY OF THESE FROM A CARD, A BANNER, A
// CAPABILITY BADGE OR THE PROFILE CARD. <<<
// `OemSupportPanel` and `OutlineCta` render the feature's TWO CTAs, and the
// locked rule is that ALL CTAs live only in the popup. Keeping them in a file
// whose header says so is the guardrail; the import graph is the proof.
//
// Nothing here derives readiness — every part takes already-resolved strings
// and booleans from ReadinessPopupV2, which reads the shared adapter.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import * as Dialog from "@radix-ui/react-dialog";

import ReadinessDots from "./ReadinessDots";
import { MachineryV2ReadinessPresentation, RD } from "./readinessPresentation";

export interface PopupHeaderProps {
  eyebrow: string;
  /** Machine or catalog-model display name — also the Radix Dialog title. */
  name: string;
  closeLabel: string;
  presentation: MachineryV2ReadinessPresentation;
}

/** Eyebrow + title + the MIDAS/LIVE/OEM pill + close. The pill is the SAME
 *  ReadinessDots the profile card uses, so the popup header and the surface
 *  that opened it always show the identical three signals. */
export const PopupHeader: React.FC<PopupHeaderProps> = ({
  eyebrow,
  name,
  closeLabel,
  presentation,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "16px 22px 12px",
      borderBottom: "1px solid var(--border-soft)",
    }}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className="eyebrow" style={{ color: "var(--muted)", marginBottom: 3 }}>
        {eyebrow}
      </div>
      <Dialog.Title asChild>
        <div
          style={{
            fontSize: 17,
            fontWeight: 700,
            color: "var(--ink)",
            letterSpacing: "-0.01em",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </div>
      </Dialog.Title>
    </div>
    <ReadinessDots presentation={presentation} pill />
    <Dialog.Close asChild>
      <button
        type="button"
        className="btn btn-ghost"
        aria-label={closeLabel}
        style={{ width: 30, padding: 0, fontSize: 15, justifyContent: "center" }}
      >
        ×
      </button>
    </Dialog.Close>
  </div>
);

/** READY / NO DOCS YET / CONNECTED / POSSIBLE / NOT CONNECTED pill. */
export const SectionBadge: React.FC<{ label: string; tone: "ready" | "muted" }> = ({
  label,
  tone,
}) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      height: 20,
      padding: "0 7px",
      borderRadius: 6,
      background:
        tone === "ready"
          ? "color-mix(in oklab, var(--green) 14%, var(--card))"
          : "var(--bg-soft)",
      border:
        "1px solid " +
        (tone === "ready"
          ? "color-mix(in oklab, var(--green) 34%, var(--border))"
          : "var(--border)"),
      color:
        tone === "ready" ? "color-mix(in oklab, var(--green) 76%, var(--ink))" : "var(--muted)",
      fontFamily: "var(--font-mono)",
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </span>
);

/** Dark gold OEM showcase — the one dark surface in a light UI, verbatim from
 *  the artboard. Carries CTA #1 ("Contact <OEM>"). OEM state only. */
export const OemSupportPanel: React.FC<{
  eyebrow: string;
  title: string;
  body: string;
  ctaLabel: string;
  onCta: () => void;
}> = ({ eyebrow, title, body, ctaLabel, onCta }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 13,
      padding: "14px 16px",
      borderRadius: 10,
      background: "linear-gradient(135deg, #23201A 0%, #2E2820 60%, #383024 100%)",
      border: "1px solid " + RD.goldLine,
    }}
  >
    <span
      aria-hidden
      style={{
        width: 38,
        height: 38,
        borderRadius: 999,
        flexShrink: 0,
        background: "linear-gradient(135deg, #F2C14E, #D9A227)",
        display: "grid",
        placeItems: "center",
        fontSize: 17,
        color: "#3A2E10",
      }}
    >
      ★
    </span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "#F2C14E",
        }}
      >
        {eyebrow}
      </div>
      <div style={{ fontSize: 12.5, color: "#D8CFBB", marginTop: 2 }}>{title}</div>
      <div style={{ fontSize: 11.5, color: "#A69B87", marginTop: 2 }}>{body}</div>
    </div>
    <button
      type="button"
      onClick={onCta}
      style={{
        flexShrink: 0,
        height: 30,
        padding: "0 14px",
        borderRadius: 999,
        border: "none",
        cursor: "pointer",
        background: "linear-gradient(135deg, #F2C14E, #D9A227)",
        fontFamily: "inherit",
        fontSize: 12.5,
        fontWeight: 700,
        color: "#3A2E10",
        whiteSpace: "nowrap",
      }}
    >
      {ctaLabel}
    </button>
  </div>
);

/** Outline CTA #2 — "Request MachineID integration". Never rendered when the
 *  machine is already connected. */
export const OutlineCta: React.FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      flexShrink: 0,
      height: 28,
      padding: "0 12px",
      borderRadius: 999,
      background: "transparent",
      border: "1px solid color-mix(in oklab, " + RD.link + " 45%, var(--border))",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 12,
      fontWeight: 600,
      color: RD.link,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </button>
);

export interface DocLaneProps {
  chipLabel: string;
  lit: boolean;
  /** Model-level language metadata. INFORMATIONAL ONLY — it never gates a
   *  lane, a badge or a banner (locked rule). */
  languages: string;
  provenance: string;
  isFirst: boolean;
}

/** One doc-type lane: chip - languages - provenance. Not-ingested lanes render
 *  DASHED, never hidden (the absence-is-visible rule, doc-list edition). */
export const DocLane: React.FC<DocLaneProps> = ({
  chipLabel,
  lit,
  languages,
  provenance,
  isFirst,
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "minmax(112px, auto) 84px 1fr",
      gap: 12,
      alignItems: "center",
      padding: "7px 0",
      borderTop: isFirst ? "none" : "1px solid var(--border-soft)",
      minWidth: 0,
    }}
  >
    <span
      style={{
        justifySelf: "start",
        padding: "3px 9px",
        borderRadius: 6,
        background: lit ? "color-mix(in oklab, " + RD.midas + " 12%, var(--card))" : "transparent",
        border: lit ? "1px solid transparent" : "1px dashed var(--border)",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        fontWeight: lit ? 600 : 400,
        color: lit ? RD.blueText : "var(--faint)",
        whiteSpace: "nowrap",
      }}
    >
      {chipLabel}
      {lit ? " ✓" : ""}
    </span>
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        letterSpacing: "0.03em",
        color: lit ? "var(--muted)" : "var(--faint)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {languages}
    </span>
    <span
      style={{
        fontSize: 11.5,
        lineHeight: 1.4,
        color: lit ? "var(--muted)" : "var(--faint)",
        minWidth: 0,
      }}
    >
      {provenance}
    </span>
  </div>
);
